import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { customerService } from '../services/customerService';
import { billingService } from '../services/billingService';
import { paymentService } from '../services/paymentService';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton
} from '@mui/material';
import {
  People,
  MonetizationOn,
  Receipt,
  Warning,
  PersonAdd,
  Description,
  Payment,
  Assessment,
  Refresh,
  ChevronRight,
  Person,
  Close,
  CalendarToday,
  CheckCircle,
  Bolt
} from '@mui/icons-material';

const DashboardHome = ({ user }) => {
  const navigate = useNavigate();
  const [recentCustomers, setRecentCustomers] = useState([]);
  const [recentPayments, setRecentPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [stats, setStats] = useState({
    totalCustomers: 0,
    activeCustomers: 0,
    monthlyRevenue: 0,
    pendingBills: 0,
    overdueAccounts: 0,
    todayCollection: 0
  });
  
  useEffect(() => {
    loadRecentData();
    loadStatistics();
  }, []);
  
  const loadRecentData = async () => {
    setLoading(true);
    try {
      const [customersResult, paymentsResult] = await Promise.all([
        customerService.getRecentCustomers(5),
        paymentService.getRecentPayments(5)
      ]);
      
      if (customersResult.success) setRecentCustomers(customersResult.data || []);
      if (paymentsResult.success) setRecentPayments(paymentsResult.data || []);
    } catch (err) {
      console.error('Error loading recent data:', err);
    }
    setLoading(false);
  };

  const loadStatistics = async () => {
    setStatsLoading(true);
    try {
      const [customerStats, paymentStats, billsResult] = await Promise.all([
        customerService.getCustomerStats(),
        paymentService.getPaymentStats(),
        billingService.getBills(1, 10000, 'paymentstatus', true)
      ]);

      const newStats = {
        totalCustomers: customerStats.data?.total || 0,
        activeCustomers: customerStats.data?.active || 0,
        monthlyRevenue: paymentStats.data?.monthCollection || 0,
        todayCollection: paymentStats.data?.todayCollection || 0,
        pendingBills: billsResult.data?.filter(b => b.paymentstatus === 'Unpaid').length || 0,
        overdueAccounts: 0 // Will be calculated
      };

      // Calculate overdue accounts (bills from 2+ months ago still unpaid)
      const twoMonthsAgo = new Date();
      twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);
      const overdueCustomerIds = new Set(
        billsResult.data
          ?.filter(b => b.paymentstatus === 'Unpaid' && new Date(b.billedmonth) < twoMonthsAgo)
          .map(b => b.customerid) || []
      );
      newStats.overdueAccounts = overdueCustomerIds.size;

      setStats(newStats);
    } catch (err) {
      console.error('Error loading statistics:', err);
    }
    setStatsLoading(false);
  };

  const StatCard = ({ title, value, subtext, icon, color, bgcolor, trend }) => (
    <Card 
      sx={{ 
        height: '100%', 
        p: 2.5,
        borderRadius: 3.5,
        border: '1px solid #e2e8f0',
        boxShadow: '0 4px 16px -2px rgba(0, 0, 0, 0.05)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 24px -4px rgba(0, 0, 0, 0.08)'
        }
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Typography 
          sx={{ 
            fontSize: '0.8125rem', 
            fontWeight: 700, 
            color: '#64748b', 
            textTransform: 'uppercase', 
            letterSpacing: '0.05em' 
          }}
        >
          {title}
        </Typography>
        <Box 
          sx={{ 
            width: 44, 
            height: 44, 
            borderRadius: 2.5, 
            bgcolor: bgcolor, 
            color: color, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }}
        >
          {React.cloneElement(icon, { sx: { fontSize: 24 } })}
        </Box>
      </Box>
      <Box>
        <Typography variant="h4" component="div" fontWeight={800} color="#0f172a" sx={{ mb: 0.5, letterSpacing: '-0.02em' }}>
          {statsLoading ? <CircularProgress size={24} sx={{ color: '#059669' }} /> : value}
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500, fontSize: '0.8125rem' }}>
          {subtext}
        </Typography>
      </Box>
    </Card>
  );

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      {/* Header Banner */}
      <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={800} color="#0f172a" sx={{ letterSpacing: '-0.02em', mb: 0.5 }}>
            Water Billing Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Welcome back, <strong style={{ color: '#059669' }}>{user?.firstname || 'Staff'}</strong>! Here's your real-time operational overview.
          </Typography>
        </Box>
        <Button 
          variant="outlined" 
          startIcon={<Refresh />} 
          onClick={() => { loadStatistics(); loadRecentData(); }}
          sx={{ 
            borderRadius: 2.5, 
            borderColor: '#e2e8f0', 
            color: '#475569',
            fontWeight: 600,
            '&:hover': { bgcolor: '#ffffff', borderColor: '#cbd5e1' }
          }}
        >
          Refresh Overview
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Consumers"
            value={stats.totalCustomers.toLocaleString()}
            subtext={`${stats.activeCustomers} Active Meter Accounts`}
            icon={<People />}
            color="#059669"
            bgcolor="#ecfdf5"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Monthly Revenue"
            value={`₱${stats.monthlyRevenue.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            subtext={`₱${stats.todayCollection.toLocaleString('en-PH', { minimumFractionDigits: 2 })} Today's Collection`}
            icon={<MonetizationOn />}
            color="#0284c7"
            bgcolor="#e0f2fe"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Unpaid Water Bills"
            value={stats.pendingBills.toLocaleString()}
            subtext="Awaiting Consumer Payment"
            icon={<Receipt />}
            color="#d97706"
            bgcolor="#fef3c7"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Overdue Accounts"
            value={stats.overdueAccounts.toLocaleString()}
            subtext="2+ Months Outstanding"
            icon={<Warning />}
            color="#dc2626"
            bgcolor="#fee2e2"
          />
        </Grid>
      </Grid>

      {/* Quick Operations Bar */}
      <Card 
        sx={{ 
          mb: 4, 
          p: 2.5, 
          borderRadius: 3.5, 
          border: '1px solid #e2e8f0', 
          bgcolor: '#ffffff',
          boxShadow: '0 4px 16px -2px rgba(0, 0, 0, 0.04)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Bolt sx={{ color: '#059669' }} />
          <Typography variant="subtitle1" fontWeight={700} color="#0f172a">
            Quick Operations
          </Typography>
        </Box>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm="auto">
            <Button 
              fullWidth 
              variant="contained" 
              startIcon={<PersonAdd />} 
              onClick={() => navigate('/portal/water-billing/customers')}
              sx={{ 
                bgcolor: '#059669', 
                color: '#ffffff', 
                borderRadius: 2.5, 
                px: 2.5, 
                py: 1,
                fontWeight: 600,
                boxShadow: '0 4px 10px rgba(5, 150, 105, 0.25)',
                '&:hover': { bgcolor: '#047857' }
              }}
            >
              Add Consumer
            </Button>
          </Grid>
          <Grid item xs={12} sm="auto">
            <Button 
              fullWidth 
              variant="outlined" 
              startIcon={<Description />} 
              onClick={() => navigate('/portal/water-billing/billing')}
              sx={{ 
                borderRadius: 2.5, 
                px: 2.5, 
                py: 1, 
                fontWeight: 600,
                borderColor: '#059669',
                color: '#059669',
                '&:hover': { bgcolor: 'rgba(5, 150, 105, 0.04)', borderColor: '#047857' }
              }}
            >
              Encode Readings & Bill
            </Button>
          </Grid>
          <Grid item xs={12} sm="auto">
            <Button 
              fullWidth 
              variant="outlined" 
              startIcon={<Payment />} 
              onClick={() => navigate('/portal/water-billing/payments')}
              sx={{ 
                borderRadius: 2.5, 
                px: 2.5, 
                py: 1, 
                fontWeight: 600,
                borderColor: '#0284c7',
                color: '#0284c7',
                '&:hover': { bgcolor: 'rgba(2, 132, 199, 0.04)', borderColor: '#0369a1' }
              }}
            >
              Receive Cashier Payment
            </Button>
          </Grid>
          <Grid item xs={12} sm="auto">
            <Button 
              fullWidth 
              variant="outlined" 
              startIcon={<Assessment />} 
              onClick={() => navigate('/portal/water-billing/reports')}
              sx={{ 
                borderRadius: 2.5, 
                px: 2.5, 
                py: 1, 
                fontWeight: 600,
                borderColor: '#64748b',
                color: '#475569',
                '&:hover': { bgcolor: '#f8faf9', borderColor: '#334155' }
              }}
            >
              Financial & Billing Reports
            </Button>
          </Grid>
        </Grid>
      </Card>

      {/* Recent Activities Section */}
      <Grid container spacing={3}>
        {/* Recent Customers */}
        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              height: '100%', 
              borderRadius: 3.5, 
              border: '1px solid #e2e8f0', 
              boxShadow: '0 4px 16px -2px rgba(0, 0, 0, 0.04)',
              overflow: 'hidden'
            }}
          >
            <Box sx={{ p: 2.5, bgcolor: '#f8faf9', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <People sx={{ color: '#059669', fontSize: 20 }} />
                <Typography variant="subtitle1" fontWeight={700} color="#0f172a">
                  Recently Enrolled Consumers
                </Typography>
              </Box>
              <Button 
                size="small" 
                onClick={() => navigate('/portal/water-billing/customers')}
                sx={{ textTransform: 'none', color: '#059669', fontWeight: 600 }}
              >
                View All
              </Button>
            </Box>
            <List sx={{ p: 1 }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress sx={{ color: '#059669' }} size={28} />
                </Box>
              ) : recentCustomers.length === 0 ? (
                <ListItem sx={{ py: 4 }}>
                  <ListItemText primary="No recent customer records found" sx={{ textAlign: 'center', color: '#64748b' }} />
                </ListItem>
              ) : (
                recentCustomers.map((customer, index) => (
                  <React.Fragment key={customer.customerid}>
                    <ListItemButton 
                      onClick={() => setSelectedCustomer(customer)}
                      sx={{ 
                        borderRadius: 2.5, 
                        py: 1.25,
                        px: 1.5,
                        transition: 'background-color 0.15s',
                        '&:hover': { bgcolor: '#f8faf9' }
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: '#ecfdf5', color: '#059669', width: 38, height: 38 }}>
                          <Person sx={{ fontSize: 20 }} />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle2" fontWeight={700} color="#0f172a">
                            {customer.name}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body2" color="#64748b" sx={{ fontSize: '0.78rem' }}>
                            <strong style={{ color: '#059669' }}>{customer.type}</strong> — {customer.address ? `${customer.address}, ${customer.barangay || ''}`.trim().replace(/(, )+$/, '') : (customer.barangay || 'No address')}
                          </Typography>
                        }
                      />
                      <ChevronRight sx={{ color: '#94a3b8', fontSize: 20 }} />
                    </ListItemButton>
                    {index < recentCustomers.length - 1 && <Divider sx={{ my: 0.5, borderColor: '#f1f5f9' }} />}
                  </React.Fragment>
                ))
              )}
            </List>
          </Card>
        </Grid>

        {/* Recent Payments */}
        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              height: '100%', 
              borderRadius: 3.5, 
              border: '1px solid #e2e8f0', 
              boxShadow: '0 4px 16px -2px rgba(0, 0, 0, 0.04)',
              overflow: 'hidden'
            }}
          >
            <Box sx={{ p: 2.5, bgcolor: '#f8faf9', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Payment sx={{ color: '#0284c7', fontSize: 20 }} />
                <Typography variant="subtitle1" fontWeight={700} color="#0f172a">
                  Recent Water Collections
                </Typography>
              </Box>
              <Button 
                size="small" 
                onClick={() => navigate('/portal/water-billing/payments')}
                sx={{ textTransform: 'none', color: '#0284c7', fontWeight: 600 }}
              >
                View All
              </Button>
            </Box>
            <List sx={{ p: 1 }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress sx={{ color: '#0284c7' }} size={28} />
                </Box>
              ) : recentPayments.length === 0 ? (
                <ListItem sx={{ py: 4 }}>
                  <ListItemText primary="No recent payments recorded" sx={{ textAlign: 'center', color: '#64748b' }} />
                </ListItem>
              ) : (
                recentPayments.map((payment, index) => (
                  <React.Fragment key={payment.billid}>
                    <ListItemButton 
                      onClick={() => setSelectedPayment(payment)}
                      sx={{ 
                        borderRadius: 2.5, 
                        py: 1.25,
                        px: 1.5,
                        transition: 'background-color 0.15s',
                        '&:hover': { bgcolor: '#f8faf9' }
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: '#e0f2fe', color: '#0284c7', width: 38, height: 38 }}>
                          <CheckCircle sx={{ fontSize: 20 }} />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle2" fontWeight={700} color="#0f172a">
                            {payment.customer?.name || 'Consumer'}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body2" color="#64748b" sx={{ fontSize: '0.78rem' }}>
                            OR #{payment.billid} • {new Date(payment.datepaid).toLocaleDateString()}
                          </Typography>
                        }
                      />
                      <Typography variant="subtitle2" fontWeight={800} color="#059669">
                        ₱{payment.totalbillamount?.toFixed(2)}
                      </Typography>
                    </ListItemButton>
                    {index < recentPayments.length - 1 && <Divider sx={{ my: 0.5, borderColor: '#f1f5f9' }} />}
                  </React.Fragment>
                ))
              )}
            </List>
          </Card>
        </Grid>
      </Grid>

      {/* Customer Detail Modal */}
      <Dialog 
        open={Boolean(selectedCustomer)} 
        onClose={() => setSelectedCustomer(null)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3.5,
            p: 1
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#0f172a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Consumer Details
          <IconButton
            aria-label="close"
            onClick={() => setSelectedCustomer(null)}
            sx={{ color: '#64748b' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: '#e2e8f0' }}>
          {selectedCustomer && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Account / Meter ID" value={selectedCustomer.customerid} InputProps={{ readOnly: true }} variant="outlined" size="small" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Classification" value={selectedCustomer.type || 'N/A'} InputProps={{ readOnly: true }} variant="outlined" size="small" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Account Holder Name" value={selectedCustomer.name} InputProps={{ readOnly: true }} variant="outlined" size="small" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Street Address" value={selectedCustomer.address || 'N/A'} InputProps={{ readOnly: true }} variant="outlined" size="small" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Barangay" value={selectedCustomer.barangay || 'N/A'} InputProps={{ readOnly: true }} variant="outlined" size="small" />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Connection Status</Typography>
                <Chip 
                  label={selectedCustomer.status || 'Active'} 
                  sx={{ 
                    bgcolor: selectedCustomer.status === 'Inactive' ? '#fee2e2' : '#ecfdf5',
                    color: selectedCustomer.status === 'Inactive' ? '#b91c1c' : '#047857',
                    fontWeight: 700
                  }} 
                />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Date Enrolled" value={selectedCustomer.date_added || 'N/A'} InputProps={{ readOnly: true }} variant="outlined" size="small" />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setSelectedCustomer(null)}
            sx={{ borderRadius: 2, color: '#64748b', fontWeight: 600 }}
          >
            Close
          </Button>
          <Button 
            variant="contained"
            onClick={() => {
              const id = selectedCustomer?.customerid;
              setSelectedCustomer(null);
              navigate(`/portal/water-billing/customers?search=${id}`);
            }}
            sx={{ 
              borderRadius: 2, 
              bgcolor: '#059669', 
              color: '#ffffff',
              fontWeight: 600,
              '&:hover': { bgcolor: '#047857' }
            }}
          >
            Open in Consumers Directory
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Detail Modal */}
      <Dialog 
        open={Boolean(selectedPayment)} 
        onClose={() => setSelectedPayment(null)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3.5,
            p: 1
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#0f172a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Official Receipt & Payment Details
          <IconButton
            aria-label="close"
            onClick={() => setSelectedPayment(null)}
            sx={{ color: '#64748b' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: '#e2e8f0' }}>
          {selectedPayment && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Bill / OR Number" value={selectedPayment.billid} InputProps={{ readOnly: true }} size="small" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Payment Date" value={selectedPayment.datepaid ? new Date(selectedPayment.datepaid).toLocaleDateString() : 'N/A'} InputProps={{ readOnly: true }} size="small" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Customer Name" value={selectedPayment.customer?.name || 'Unknown'} InputProps={{ readOnly: true }} size="small" />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Billed Month" value={selectedPayment.billedmonth || 'N/A'} InputProps={{ readOnly: true }} size="small" />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Processed By Cashier" value={selectedPayment.paidby || 'Staff'} InputProps={{ readOnly: true }} size="small" />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Basic Water Amount" value={`₱${selectedPayment.basicamount?.toFixed(2) || '0.00'}`} InputProps={{ readOnly: true }} size="small" />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Surcharges" value={`₱${selectedPayment.surchargeamount?.toFixed(2) || '0.00'}`} InputProps={{ readOnly: true }} size="small" />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ p: 2, bgcolor: '#ecfdf5', borderRadius: 2.5, border: '1px solid #a7f3d0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle2" color="#065f46" fontWeight={700}>
                    Total Amount Settled
                  </Typography>
                  <Typography variant="h5" color="#047857" fontWeight={800}>
                    ₱{selectedPayment.totalbillamount?.toFixed(2) || '0.00'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setSelectedPayment(null)}
            sx={{ borderRadius: 2, color: '#64748b', fontWeight: 600 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DashboardHome;
